import React from "react";
import dayjs from "dayjs";
import { Box, Stack, Typography } from "@mui/material";

import { DataRateChange, HubIcon } from "../../components";
import { StyledBarTooltip } from "./styles";

const currency = (v) => {
  if (v == null || isNaN(v)) return "$0";
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}K`;
  return `$${Math.round(v)}`;
};

const getRate = (curr, prev) => {
  if (!prev) return undefined;
  return Math.round(((curr - prev) / prev) * 10000) / 100;
};

const MetricRow = ({ color, label, value, rate, sinceLabel }) => (
  <Stack direction="row" gap="10px" alignItems="center">
    <Box
      sx={{
        width: "4px",
        height: "44px",
        borderRadius: "3px",
        backgroundColor: color,
      }}
    />
    <Stack gap="2px">
      <Typography
        variant="caption"
        sx={{ fontSize: 10, color: "text.secondary" }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
        {currency(value)}
      </Typography>
      {rate !== undefined ? (
        <DataRateChange rate={rate} label={sinceLabel} />
      ) : (
        <Typography
          variant="caption"
          sx={{ fontSize: 9, color: "text.disabled" }}
        >
          {sinceLabel}: —
        </Typography>
      )}
    </Stack>
  </Stack>
);

const GapRow = ({ color, value, delta }) => {
  if (value == null) return null;
  const ahead = value >= 0;
  const accent = ahead ? "success.main" : "error.main";
  return (
    <Stack direction="row" gap="10px" alignItems="center">
      <Box
        sx={{
          width: "4px",
          height: "44px",
          borderRadius: "3px",
          backgroundColor: color,
        }}
      />
      <Stack gap="2px">
        <Typography
          variant="caption"
          sx={{ fontSize: 10, color: "text.secondary" }}
        >
          {ahead ? "Ahead of Goal" : "Behind Goal"}
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: 13, color: accent }}>
          {ahead ? "+" : "-"}
          {currency(Math.abs(value))}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontSize: 9, color: "text.disabled" }}
        >
          {delta == null
            ? "Cumulative vs daily goal"
            : `Today: ${delta >= 0 ? "+" : "-"}${currency(Math.abs(delta))}`}
        </Typography>
      </Stack>
    </Stack>
  );
};

const buildDayWeekIndex = (days = []) => {
  const dayToWeek = {};
  const weeks = [];
  let current = null;
  days.forEach((d, i) => {
    const day = dayjs.utc(d);
    if (i === 0 || day.day() === 0) {
      current = { dayKeys: [] };
      weeks.push(current);
    }
    current.dayKeys.push(d);
    dayToWeek[d] = weeks.length - 1;
  });
  return { dayToWeek, weeks };
};

export const DailyChartTooltip = ({
  axisValue,
  dataIndex,
  series = [],
  days = [],
  weekLabels = {},
}) => {
  const dayKey = axisValue || days[dataIndex];
  if (!dayKey) return null;

  const { dayToWeek, weeks } = buildDayWeekIndex(days);
  const weekIdx = dayToWeek[dayKey];
  const week = weeks[weekIdx];
  const prevWeek = weeks[weekIdx - 1];

  const revenueSeries =
    series.find((s) => s?.dataKey === "revenue") || series[0];
  const gapSeries = series.find((s) => s?.dataKey === "gap");
  const data = revenueSeries?.data || [];

  const dailyValue = data[dataIndex] || 0;
  const prevDailyValue = dataIndex > 0 ? data[dataIndex - 1] || 0 : undefined;
  const dailyRate = getRate(dailyValue, prevDailyValue);

  const sumWeek = (w) =>
    w ? w.dayKeys.reduce((s, d) => s + (data[days.indexOf(d)] || 0), 0) : 0;
  const weeklyValue = sumWeek(week);
  const prevWeeklyValue = prevWeek ? sumWeek(prevWeek) : undefined;
  const weeklyRate =
    prevWeeklyValue !== undefined
      ? getRate(weeklyValue, prevWeeklyValue)
      : undefined;

  const gapData = gapSeries?.data || [];
  const gapValue = gapData[dataIndex];
  const prevGapValue = dataIndex > 0 ? gapData[dataIndex - 1] : null;
  const gapDelta =
    gapValue != null && prevGapValue != null ? gapValue - prevGapValue : null;

  const weekLabel =
    week &&
    (weekLabels[week.dayKeys[Math.floor(week.dayKeys.length / 2)]] ||
      `${dayjs.utc(week.dayKeys[0]).format("MMM D")} - ${dayjs
        .utc(week.dayKeys[week.dayKeys.length - 1])
        .format("MMM D")}`);
  const dayLabel = dayjs.utc(dayKey).format("ddd, MMM D");

  return (
    <StyledBarTooltip>
      <Stack
        gap="2px"
        pb="8px"
        borderBottom="1px solid"
        borderColor="hubColors.greyLight"
      >
        <Stack direction="row" gap="6px" alignItems="center">
          <HubIcon icon="Calendar" size={11} />
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12 }}>
            {weekLabel}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: 10, pl: "17px" }}
        >
          {dayLabel}
        </Typography>
      </Stack>

      <Stack gap="10px" pt="10px">
        <MetricRow
          color={revenueSeries?.color || "#df9e44"}
          label="Daily Revenue"
          value={dailyValue}
          rate={dailyRate}
          sinceLabel="Since Last Day"
        />
        <MetricRow
          color="#5B9BD5"
          label="Weekly Revenue"
          value={weeklyValue}
          rate={weeklyRate}
          sinceLabel="Since Last Week"
        />
        <GapRow
          color={gapSeries?.color || "#1F4E79"}
          value={gapValue}
          delta={gapDelta}
        />
      </Stack>
    </StyledBarTooltip>
  );
};
